import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  brandingSettingsApi,
  companySettingsApi,
  emailSettingsApi,
  whatsAppSettingsApi,
} from '@/api/settings.api';
import { qk } from '@/config/queryKeys';
import type {
  UpsertBrandingSettingsRequest,
  UpsertCompanySettingsRequest,
  UpsertEmailSettingsRequest,
  UpsertWhatsAppSettingsRequest,
} from '@/types/settings';

// ---- Company ----
export function useCompanySettings() {
  return useQuery({ queryKey: qk.settings.company, queryFn: companySettingsApi.get });
}
export function useUpdateCompanySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: UpsertCompanySettingsRequest) => companySettingsApi.update(b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.settings.company });
      // Company name feeds public branding (login/sidebar/header/portal) → refresh it everywhere.
      qc.invalidateQueries({ queryKey: ['public-branding'] });
    },
  });
}

// ---- Branding ----
export function useBrandingSettings() {
  return useQuery({ queryKey: qk.settings.branding, queryFn: brandingSettingsApi.get });
}
export function useUpdateBrandingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: UpsertBrandingSettingsRequest) => brandingSettingsApi.update(b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.settings.branding });
      // Refresh branding across every surface that reads the public branding/logo.
      qc.invalidateQueries({ queryKey: ['public-branding'] });
      qc.invalidateQueries({ queryKey: ['branding-logo-blob'] });
    },
  });
}

// ---- Email ----
export function useEmailSettings() {
  return useQuery({ queryKey: qk.settings.email, queryFn: emailSettingsApi.get });
}
export function useUpdateEmailSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: UpsertEmailSettingsRequest) => emailSettingsApi.update(b), onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.email }) });
}

// ---- WhatsApp ----
export function useWhatsAppSettings() {
  return useQuery({ queryKey: qk.settings.whatsapp, queryFn: whatsAppSettingsApi.get });
}
export function useUpdateWhatsAppSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (b: UpsertWhatsAppSettingsRequest) => whatsAppSettingsApi.update(b), onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.whatsapp }) });
}
